/* eslint-disable react-hooks/exhaustive-deps, jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Divider,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  Zoom,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useUser } from "../../hooks/Authorize";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import { getCookie } from "../../utilities/cookies";

const OfferPayment = () => {
  const stripe = useStripe();
  const elements = useElements();
  const platformUser = useUser();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("planId");
  const orgId = searchParams.get("orgId");
  const navigate = useNavigate();
  console.log(platformUser)
  const [plan, setPlan] = useState(null);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [billingAddress, setFormData] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [seats, setSeats] = useState(5);

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const orgBasedApps = ["hr", "projects", "project", "chat", "campaigns", "campaign", "worksuite", "app"];
  const isOrgBased = orgBasedApps.includes(plan?.type?.toLowerCase());

  const handleSeatsChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      setSeats(value);
    }
  };

  const handleSeatsBlur = () => {
    if (seats < 5) {
      setSeats(5);
      setSnackbar({ open: true, message: "Minimum 5 seats are required for organization-based plans.", severity: "warning" });
    }
  };

  const totalAmount = plan ? (isOrgBased && plan.interval !== "one-time" ? plan.amount * seats : plan.amount) : 0;
  const isOneTime = plan?.interval === "one-time";

  useEffect(() => {
    const fetchData = async () => {
      if (!planId) {
        navigate("/myStore");
        return;
      }

      try {
        const planRes = await axios.get(`/store/plan/${planId}`);
        if (planRes.data.success) {
          const planData = planRes.data.data;
          setPlan(planData);

          const orgBasedAppsList = ["hr", "projects", "project", "chat", "campaigns", "worksuite", "app"];
          const isOrgBasedPlan = orgBasedAppsList.includes(planData.type?.toLowerCase());

          if (isOrgBasedPlan && !orgId) {
            setRedirecting(true);
            setSnackbar({ open: true, message: "Can't Find Organization. Redirecting to My Store...", severity: "info" });
            setTimeout(() => {
              window.location.href = "/myStore";
            }, 1500);
            return;
          }

          try {
            const offerRes = await axios.get(`https://api.store.clikkle.com/store/plan/offer-by-plan/${planId}`);
            if (offerRes.data.success) {
              setOffer(offerRes.data.data);
            }
          } catch (offerErr) {
            console.warn("No offer found for this plan, showing plan details only.");
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({ open: true, message: "Failed to load plan details.", severity: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [planId]);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !plan) return;

    if (!cardholderName) {
      setSnackbar({ open: true, message: "Please enter the cardholder name.", severity: "warning" });
      return;
    }

    if (isOrgBased && seats < 5) {
      setSnackbar({ open: true, message: "Minimum 5 seats are required.", severity: "warning" });
      return;
    }

    setProcessing(true);
    const cardElement = elements.getElement(CardNumberElement);

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: cardholderName,
          email: platformUser?.recoveryEmail || platformUser?.email,
          address: billingAddress,
        },
      });

      if (pmError) throw pmError;

      const { token, error: tokenError } = await stripe.createToken(cardElement);
      if (tokenError) throw tokenError;

      const payload = {
        userId: platformUser?._id,
        name: cardholderName,
        email: platformUser?.recoveryEmail || platformUser?.email,
        paymentMethod: paymentMethod.id,
        tokenId: token.id,
        card: token.card,
        billing_details: {
          address: billingAddress,
          name: cardholderName,
          email: platformUser?.recoveryEmail || platformUser?.email,
        },
        plan: plan.name,
        planId: plan._id,
        amount: totalAmount,
        noOfUsers: isOneTime ? 1 : (isOrgBased ? seats : 1),
        orgId: orgId,
        billingCycle: plan.interval === "year" ? "Yearly" : plan.interval === "month" ? "Monthly" : "One-time",
      };

      const response = await axios.post(
        `/store/subscription/checkout`,
        payload
      );

      if (response.status === 200) {
        localStorage.setItem("subscriptionId", response.data.subscriptionId);
        setShowSuccessPopup(true);

        let timer = 3;
        const intervalId = setInterval(() => {
          timer -= 1;
          setCountdown(timer);
          if (timer <= 0) {
            clearInterval(intervalId);
            const type = plan?.type?.toLowerCase();
            let redirectUrl = "https://app.clikkle.com/";

            const subdomains = {
              hr: "https://hr.clikkle.com/login",
              projects: "https://projects.clikkle.com/login",
              chat: "https://chat.clikkle.com/login",
              campaigns: "https://campaigns.clikkle.com/login",
              worksuite: "https://app.clikkle.com/login",
              files: "https://files.clikkle.com/login",
              esign: "https://esign.clikkle.com/login",
            };

            if (subdomains[type]) {
              redirectUrl = subdomains[type];
            }
            let refreshToken = platformUser?.refreshToken;

            const cleanToken = (token) => {
              if (!token || token === "undefined") return null;
              return token.replace(/^"|"$/g, "");
            };

            const accessToken = cleanToken(getCookie("clikkleStoreAccessToken"));
            if (accessToken) {
              refreshToken = accessToken;
            } else {
              const someToken = cleanToken(localStorage.getItem("refreshToken"));
              if (someToken) {
                refreshToken = someToken;
              }
            }
            const userId = platformUser?._id;


            if (userId && refreshToken) {
              const separator = redirectUrl.includes("?") ? "&" : "?";
              redirectUrl = `${redirectUrl}${separator}userId=${userId}&refreshToken=${refreshToken}`;
            }

            window.location.replace(redirectUrl);
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Payment error:", error);
      setSnackbar({ open: true, message: error.response?.data?.message || error.message || "Payment failed.", severity: "error" });
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
    if (offer?.slug) {
      window.location.href = `https://worksuite.clikkle.com/offers/${offer.slug}`;
    } else {
      navigate(-1);
    }
  };

  if (loading || redirecting) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="#fff"
        gap={2}
      >
        <CircularProgress />
        {redirecting && (
          <Typography variant="h6" sx={{ color: "#1a1f36" }}>
            Redirecting to My Store...
          </Typography>
        )}
      </Box>
    );
  }

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#1a1f36",
        "::placeholder": { color: "#aab7c4" },
        fontFamily: "Inter, system-ui, sans-serif",
      },
      invalid: { color: "#9e2146" },
    },
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      minHeight: "100vh",
      bgcolor: "#fff",
      color: "#1a1f36",
      position: "relative",
      overflow: "hidden"
    }}>
      {isOneTime && (
        <Box sx={{
          position: "absolute",
          top: 40,
          right: -40,
          width: 200,
          height: 32,
          bgcolor: "#0048e5",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: "rotate(45deg)",
          zIndex: 10,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          pointerEvents: "none"
        }}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
            Lifetime Plan
          </Typography>
        </Box>
      )}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>

      {/* Left Side: Product Info */}
      <Box sx={{
        width: { xs: "100%", md: "45%" },
        p: { xs: 4, sm: 6, md: 8 },
        borderRight: { md: "1px solid #e3e8ee" },
        borderBottom: { xs: "1px solid #e3e8ee", md: "none" },
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f7f9fc"
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ alignSelf: "flex-start", mb: { xs: 4, md: 6 }, color: "#697386", textTransform: "none", "&:hover": { bgcolor: "transparent", color: "#1a1f36" } }}
        >
          Back
        </Button>

        <Box sx={{ mb: 4, display: "flex", alignItems: "center" }}>
          <img
            alt="Clikkle logo"
            style={{ height: "40px", width: "auto" }}
            src={
              offer?.logos?.light ||
              "https://cdn.clikkle.com/images/worksuite/2025/Clikkle%20Worksuite%20Blue%20(%20Black%20Text).png"
            }
          />
        </Box>

        <Box sx={{ mb: 0 }}>
          {isOneTime && (
            <Box sx={{
              display: "inline-flex",
              alignItems: "center",
              bgcolor: "rgba(0, 72, 229, 0.08)",
              color: "#0048e5",
              px: 1.5,
              py: 0.5,
              borderRadius: "4px",
              mb: 2,
              border: "1px solid rgba(0, 72, 229, 0.15)"
            }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
                Lifetime Deal
              </Typography>
            </Box>
          )}
          {plan?.title && (
            <Typography variant="overline" sx={{ color: "#697386", fontWeight: 600, letterSpacing: 1.2, display: "block", mb: 0.5 }}>
              {plan.title}
            </Typography>
          )}
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: "#1a1f36", fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" } }}>
            Clikkle {plan?.type ? plan?.type?.charAt(0).toUpperCase() + plan?.type?.slice(1) : ""} {plan?.name || offer?.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a1f36", fontSize: { xs: "1.5rem", md: "2rem" } }}>
              ${plan?.amount}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#697386", fontSize: { xs: "0.9rem", md: "1rem" }, ml: 0.5 }}>
              {isOneTime ? "/one-time" : (isOrgBased ? "/seat" : "") + (plan?.interval === "one-time" ? " /one-time" : ` /${plan?.interval}`)}
            </Typography>
          </Box>
          {plan?.description && (
            <Typography variant="body2" sx={{ color: "#4f566b", mb: 3, lineHeight: 1.6, fontSize: "0.9375rem" }}>
              {plan.description}
            </Typography>
          )}

          {isOrgBased && !isOneTime && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1f36" }}>
                  Number of seats
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={seats}
                  onChange={handleSeatsChange}
                  onBlur={handleSeatsBlur}
                  inputProps={{ min: 5, style: { padding: '8px 12px' } }}
                  sx={{
                    width: "80px",
                    border: "1px solid #e3e8ee",
                    borderRadius: "6px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      bgcolor: "#fff",
                    },
                    "& .MuiInputBase-input": {
                      fontWeight: 600,
                      textAlign: "center",
                      fontSize: "0.9rem",
                      color: "#1a1f36"
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderTop: "1px dashed #e3e8ee", borderBottom: "1px dashed #e3e8ee" }}>
                <Typography variant="body2" sx={{ color: "#4f566b", fontWeight: 500 }}>
                  Total subscription amount
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1f36", fontSize: "1.1rem" }}>
                  ${totalAmount.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}

          {isOneTime && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderTop: "1px dashed #e3e8ee", borderBottom: "1px dashed #e3e8ee" }}>
                <Typography variant="body2" sx={{ color: "#4f566b", fontWeight: 500 }}>
                  Total amount
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1f36", fontSize: "1.1rem" }}>
                  ${totalAmount.toFixed(2)}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#1a1f36", fontWeight: 600, display: "block", mt: 1.5, textAlign: "center" }}>
                One-time payment • Lifetime access • No monthly fees
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 4, flex: 1, overflowY: "auto", pr: 1, "&::-webkit-scrollbar": { width: "4px" }, "&::-webkit-scrollbar-thumb": { bgcolor: "#e3e8ee", borderRadius: "4px" } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "#1a1f36" }}>Included in this plan:</Typography>
          <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
            {plan?.permission?.filter(p => p.isPermission).map((perm, i) => (
              <Box key={i} component="li" sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
                <CheckIcon sx={{ color: "#3c4257", fontSize: 18, mr: 1.5, mt: 0.3 }} />
                <Typography variant="body2" sx={{ color: "#4f566b", fontSize: { xs: "0.85rem", md: "0.9rem" } }}>{perm.content}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ mt: { xs: 4, md: "auto" }, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>

          <Typography variant="caption" sx={{ color: "#697386", textDecoration: "none" }} component="a" href="https://worksuite.clikkle.com/terms-of-service" target="_blank" rel="noopener noreferrer">
            Terms
          </Typography>

          <Divider orientation="vertical" flexItem sx={{ height: 12, my: "auto", display: { xs: "none", sm: "block" } }} />

          <Typography variant="caption" sx={{ color: "#697386", textDecoration: "none" }} component="a" href="https://worksuite.clikkle.com/privacy-policy" target="_blank" rel="noopener noreferrer">
            Privacy
          </Typography>
        </Box>
      </Box>

      {/* Right Side: Payment Fields */}
      <Box sx={{
        width: { xs: "100%", md: "55%" },
        p: { xs: 4, sm: 6, md: 8 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}>
        <Box component="form" onSubmit={handlePayment} sx={{ maxWidth: 480, width: "100%", mx: "auto" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: "#1a1f36", fontSize: { xs: "1.25rem", md: "1.5rem" } }}>
            Payment details
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: "#4f566b" }}>Contact information</Typography>
            <TextField
              fullWidth
              variant="outlined"
              label="Email"
              value={platformUser?.recoveryEmail || platformUser?.email || ""}
              disabled
              size="small"
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, fontSize: 18, color: "#697386" }} />,
              }}
              sx={{
                // 1. Fix the Label color
                "& .MuiInputLabel-root.Mui-disabled": {
                  color: "#697386", // Or whatever color you prefer for the label
                },
                // 2. Fix the Input text color
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  "& .MuiOutlinedInput-input": {
                    color: "#1a1f36",
                    WebkitTextFillColor: "#1a1f36", // Fixes visibility in Safari/Chrome
                  },
                  // 3. Keep the border visible
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e3e8ee !important",
                  },
                },
                bgcolor: "#f7f9fc",
              }}
            />
            <Typography variant="caption" sx={{ color: "#697386", mt: 0, display: "block" }}>
              Confirmations will be sent to your recovery email.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#4f566b" }}>Card information</Typography>
            <Box sx={{ border: "1px solid #e3e8ee", borderRadius: "4px", p: "12px 16px", mb: 2 }}>
              <CardNumberElement options={cardElementOptions} />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1, border: "1px solid #e3e8ee", borderRadius: "4px", p: "12px 16px" }}>
                <CardExpiryElement options={cardElementOptions} />
              </Box>
              <Box sx={{ flex: 1, border: "1px solid #e3e8ee", borderRadius: "4px", p: "12px 16px" }}>
                <CardCvcElement options={cardElementOptions} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#4f566b" }}>Cardholder name</Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Name on card"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              size="small"
              sx={{
                "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#4f566b" }}>Billing address</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="line1"
                  placeholder="Address line 1"
                  value={billingAddress.line1}
                  onChange={handleBillingChange}
                  sx={{
                    "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
                  }} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="line2"
                  placeholder="Address line 2"
                  value={billingAddress.line2}
                  onChange={handleBillingChange}
                  sx={{
                    "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
                  }} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="city"
                  placeholder="City"
                  value={billingAddress.city}
                  onChange={handleBillingChange}
                  sx={{
                    "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
                  }} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="postal_code"
                  placeholder="Postal code"
                  value={billingAddress.postal_code}
                  onChange={handleBillingChange}
                  sx={{
                    "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
                  }} />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#4f566b" }}>Promotion code</Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              size="small"
              sx={{
                "& .MuiOutlinedInput-input": {
                  color: "#1a1f36",
                  textTransform: "uppercase"
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
              }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={processing || !stripe}
            sx={{
              bgcolor: "#0048e5",
              color: "#fff",
              py: 1.5,
              minHeight: "48px", // Ensure consistent height
              borderRadius: "4px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "16px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              "&:hover": { bgcolor: "#0039b3" },
              "&.Mui-disabled": {
                bgcolor: processing ? "#0048e5" : "#e3e8ee", // Keep color if processing
                color: processing ? "#fff" : "rgba(0, 0, 0, 0.26)",
                opacity: processing ? 0.8 : 1
              }
            }}
          >
            {processing ? <CircularProgress size={24} sx={{ color: "inherit" }} /> : (isOneTime ? "Complete purchase" : "Subscribe")}
          </Button>

          <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 3, color: "#697386" }}>
            By clicking {isOneTime ? "Complete purchase" : "Subscribe"}, you agree to Clikkle's <a href="https://worksuite.clikkle.com/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: "#0048e5", textDecoration: "none" }}>terms and conditions</a>.
          </Typography>
        </Box>
      </Box>

      {/* Success Popup - Always Light Theme */}
      <Dialog
        open={showSuccessPopup}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 2,
            textAlign: "center",
            maxWidth: "400px",
            bgcolor: "#fff", // Force white background
            color: "#1a1f36", // Force dark text
          }
        }}
      >
        <DialogContent sx={{ bgcolor: "#fff" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e6f4ea",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1
              }}
            >
              <CheckIcon sx={{ color: "#1e8e3e", fontSize: 35 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1f36" }}>
              Welcome to Clikkle!
            </Typography>
            <Typography variant="body1" sx={{ color: "#4f566b" }}>
              Your payment was successful. You're being redirected to your app in <strong>{countdown}</strong> seconds.
            </Typography>
            <CircularProgress
              variant="determinate"
              value={(countdown / 3) * 100}
              sx={{ color: "#0048e5", mt: 1 }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OfferPayment;
